import { useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useCart } from 'hooks/use-cart';
import { emptyCartContext, useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { OrderStatus, useOrderContext } from 'state/order';

export const useIsStoreUnavailable = () => {
  const { isSelectingStore, isStoreAvailable, isFetchingStore } = useStoreContext();
  const { calculateCartTotalWithDiscount } = useCart();
  const { fetchingPosData } = useOrderContext();
  const {
    serverOrder: serverOrderCart,
    unavailableCartEntries,
    priceOrderFailure,
    priceMutationError,
    orderStatus,
    shouldSkipPriceOrder,
    hydrationStatus,
    isHydrationPending,
  } = useCartContext();
  const { serverOrder: serverOrderContext } = useOrderContext();
  const serverOrder = serverOrderCart || serverOrderContext;
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { isCartTotalNegative } = calculateCartTotalWithDiscount();
  const isInCart = useCartContext() !== emptyCartContext;
  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading =
    isInCart && !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const isPriceLoading = isOrderPriceLoading || evaluatingIncentives;

  const isLoadingFinished =
    !isPriceLoading &&
    !isHydrationPending &&
    !isSelectingStore &&
    !fetchingPosData &&
    !isFetchingStore;

  const hasPricingErrors = Boolean(
    priceMutationError || priceOrderFailure || hydrationStatus?.error
  );

  return useMemo(() => {
    if (!isLoadingFinished) {
      return false;
    }

    const hasUnavailableItems = unavailableCartEntries?.length > 0;
    const hasStoreOrPricingIssues = !isStoreAvailable || hasPricingErrors;

    return !hasUnavailableItems && hasStoreOrPricingIssues;
  }, [isStoreAvailable, isLoadingFinished, unavailableCartEntries?.length, hasPricingErrors]);
};
